import type {
  Event as UserEvent,
  Entry as UserEntry,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"
import type { MangoQuery } from "rxdb"
import { useFollowedUsers } from "src/contexts"
import { useFindUserEntries } from "src/hooks/userEntry"
import { useFindUserEvents } from "src/hooks/userEvent"
import {
  createEntryQueryObjectForType,
  createEventQueryObjectForType,
} from "src/utils"

/*
 * this hook returns:
 * - the timestamp of the next glucose reading after the given timestamp
 * - the first glucose reading if the timestamp is null
 * - null if there are no glucose readings after the given timestamp
 * - undefined if the timestamp is undefined
 * - undefined if there is no glucose data
 * - undefined if loading
 */
export function useNextGlucoseReading(
  hasGlucoseData: boolean | undefined,
  afterTime: string | null | undefined,
): string | null | undefined {
  const { currentFollow } = useFollowedUsers()
  const accountId = currentFollow.following_account_id
  const afterTimeString = afterTime ?? ""

  const nextGlucoseEventSelector: MangoQuery<UserEvent> = useMemo(
    () => createEventQueryObjectForType(
      accountId,
      ActivityType.Glucose,
      {
        selector: {
          started_at: { $gt: afterTimeString ?? "" },
        },
        sort: [{ started_at: "asc" as const }],
        limit: 1,
      },
    ),
    [
      accountId,
      afterTimeString,
    ],
  )

  const nextGlucoseEntrySelector: MangoQuery<UserEntry> = useMemo(
    () => createEntryQueryObjectForType(
      accountId,
      ActivityType.Glucose,
      {
        selector: {
          started_at: { $gt: afterTimeString ?? "" },
        },
        sort: [{ started_at: "asc" as const }],
        limit: 1,
      },
    ),
    [
      accountId,
      afterTimeString,
    ],
  )

  const {
    result: nextGlucoseEventResult,
    isFetching: eventIsFetching,
  } = useFindUserEvents(nextGlucoseEventSelector)

  const {
    result: nextGlucoseEntryResult,
    isFetching: entryIsFetching,
  } = useFindUserEntries(nextGlucoseEntrySelector)

  return useMemo(
    () => {
      if (entryIsFetching || eventIsFetching) {
        return undefined
      }

      if (afterTime === undefined) {
        return undefined
      }

      if (!hasGlucoseData) {
        return undefined
      }

      const nextGlucoseEventResultStartedAt = nextGlucoseEventResult[0]?.started_at
      const nextGlucoseEntryResultStartedAt = nextGlucoseEntryResult[0]?.started_at

      if (!nextGlucoseEntryResultStartedAt && !nextGlucoseEventResultStartedAt) {
        return null
      }

      if (!nextGlucoseEntryResultStartedAt) {
        return nextGlucoseEventResultStartedAt
      }

      if (!nextGlucoseEventResultStartedAt) {
        return nextGlucoseEntryResultStartedAt
      }

      if (dayjs(nextGlucoseEntryResultStartedAt).isBefore(nextGlucoseEventResultStartedAt)) {
        return nextGlucoseEntryResultStartedAt
      }

      return nextGlucoseEventResultStartedAt
    },
    [
      nextGlucoseEventResult,
      nextGlucoseEntryResult,
      eventIsFetching,
      entryIsFetching,
      afterTime,
      hasGlucoseData,
    ],
  )
}
